// Template-based Suno prompt generator (Fake-Demo-KI)
// Returns a polished, ready-to-paste Suno music prompt string.

const GENRE_DETAILS = {
  "Dance Pop": {
    en: "modern dance pop, polished radio production, bright synth leads, four-on-the-floor kick, sidechain pumping bass, crisp vocal chops, festival energy",
    de: "moderner Dance Pop, glänzende Radio-Produktion, helle Synth-Leads, Four-on-the-Floor Kick, pumpender Sidechain-Bass, knackige Vocal Chops, Festival-Energie",
    drums: "punchy 909 kick, clap on 2 & 4, tight hi-hats, riser into drops",
    bpmDefault: 124,
  },
  "Chillout": {
    en: "downtempo chillout, lush analog pads, warm Rhodes piano, mellow saxophone touches, soft vinyl crackle, cinematic reverb, sunset balcony mood",
    de: "Downtempo Chillout, üppige Analog-Pads, warmes Rhodes Piano, sanfte Saxophon-Akzente, leiser Vinyl-Knistern, kinoreifer Hall, Sonnenuntergangs-Stimmung",
    drums: "soft brush drums, lazy swing groove, deep sub bass, finger snaps",
    bpmDefault: 92,
  },
  "Boom Bap": {
    en: "classic 90s boom bap hip hop, dusty vinyl samples, jazz piano loop, head-nodding swing, vintage SP-1200 drum textures",
    de: "klassischer 90er Boom Bap Hip Hop, staubige Vinyl-Samples, Jazz-Piano-Loop, kopfnickender Swing, vintage SP-1200 Drum-Texturen",
    drums: "hard kick, snappy snare on 2 & 4, swung 16th hi-hats, occasional vinyl crackle",
    bpmDefault: 90,
  },
  "Cinematic Trap": {
    en: "epic cinematic trap, orchestral strings, hybrid orchestral hits, dark 808 sub bass, triplet hi-hats, hero-movie tension",
    de: "epischer cinematischer Trap, orchestrale Streicher, hybride Orchester-Hits, dunkler 808 Sub-Bass, Triolen-Hi-Hats, Hollywood-Thriller-Spannung",
    drums: "booming 808 with slides, snappy trap snare, rolling triplet hats, deep tom hits",
    bpmDefault: 140,
  },
  "Deep House": {
    en: "deep house groove, warm filtered chords, soulful vocal hooks, rolling sub bass, ibiza late-night vibe",
    de: "Deep House Groove, warm gefilterte Chords, soulige Vocal Hooks, rollender Sub-Bass, ibizenkische Late-Night-Atmosphäre",
    drums: "deep house kick, shaker pattern, soft clap, congas, off-beat open hi-hat",
    bpmDefault: 122,
  },
  "Ambient": {
    en: "atmospheric ambient soundscape, evolving granular textures, floating drones, distant field recordings, meditative spaciousness",
    de: "atmosphärischer Ambient-Klanggemälde, sich entwickelnde granulare Texturen, schwebende Drones, ferne Field Recordings, meditative Weite",
    drums: "no drums, gentle ticking percussion only, organic foley",
    bpmDefault: 70,
  },
  "Kinderlied": {
    en: "playful childrens song, ukulele and glockenspiel, friendly melodic singalong hooks, hand-clap rhythm, bright and warm",
    de: "verspieltes Kinderlied, Ukulele und Glockenspiel, freundliche melodische Mitsing-Hooks, Händeklatsch-Rhythmus, hell und warm",
    drums: "soft toy drums, tambourine, finger snaps, hand claps",
    bpmDefault: 110,
  },
};

const MOOD_MAP = {
  emotional: { en: "deeply emotional, heartfelt, intimate", de: "tief emotional, gefühlvoll, intim" },
  fröhlich: { en: "happy, uplifting, joyful, sunny", de: "fröhlich, beschwingt, sonnig, optimistisch" },
  melancholisch: { en: "melancholic, bittersweet, nostalgic", de: "melancholisch, wehmütig, nostalgisch" },
  episch: { en: "epic, cinematic, grand, powerful", de: "episch, kinoreif, grandios, kraftvoll" },
  düster: { en: "dark, brooding, mysterious, shadowy", de: "düster, brütend, mysteriös, schattenhaft" },
  motivierend: { en: "motivating, energetic, empowering, driving forward", de: "motivierend, energiegeladen, kraftvoll vorwärtstreibend" },
};

const STRUCTURE_EN = "Intro -> Verse -> Pre-Chorus -> Chorus -> Verse 2 -> Chorus -> Bridge -> Final Chorus -> Outro";
const STRUCTURE_DE = "Intro -> Strophe -> Pre-Chorus -> Refrain -> Strophe 2 -> Refrain -> Bridge -> Finaler Refrain -> Outro";

export function generateSunoPrompt({ genre, mood, bpm, language, instrumental, extras }) {
  const g = GENRE_DETAILS[genre] || GENRE_DETAILS["Dance Pop"];
  const m = MOOD_MAP[mood] || MOOD_MAP["motivierend"];
  const lang = language === "Englisch" ? "en" : "de";
  const finalBpm = bpm && Number(bpm) > 0 ? Number(bpm) : g.bpmDefault;

  const isEn = lang === "en";
  const langLabel = isEn ? "English vocals" : "German vocals";
  const instrLine = instrumental
    ? isEn
      ? "Instrumental only — no vocals, no lyrics, fully arranged instrumental track."
      : "Nur instrumental – kein Gesang, keine Lyrics, vollständig arrangierter Instrumentaltrack."
    : isEn
      ? `Lead vocal: ${langLabel}, expressive performance, layered backing harmonies on chorus.`
      : `Lead-Vocal: ${langLabel}, ausdrucksstarker Gesang, geschichtete Background-Harmonien im Refrain.`;

  const styleLine = isEn ? g.en : g.de;
  const moodLine = isEn ? m.en : m.de;
  const drumsLine = g.drums;
  const structure = isEn ? STRUCTURE_EN : STRUCTURE_DE;

  const header = isEn ? "[Style]" : "[Stil]";
  const moodH = isEn ? "[Mood]" : "[Stimmung]";
  const tempoH = isEn ? "[Tempo]" : "[Tempo]";
  const instrH = isEn ? "[Instrumentation]" : "[Instrumentierung]";
  const vocalH = isEn ? "[Vocals]" : "[Gesang]";
  const structureH = isEn ? "[Structure]" : "[Struktur]";
  const mixH = isEn ? "[Mix]" : "[Mix]";
  const extrasH = isEn ? "[Extra]" : "[Zusatz]";

  const mixDesc = isEn
    ? "loud modern master, wide stereo, punchy low-end, airy top, club-ready loudness"
    : "lauter moderner Master, breites Stereobild, druckvoller Bass, luftige Höhen, club-tauglich";

  let prompt = `${header} ${styleLine}.
${moodH} ${moodLine}.
${tempoH} ${finalBpm} BPM.
${instrH} ${drumsLine}.
${vocalH} ${instrLine}
${structureH} ${structure}.
${mixH} ${mixDesc}.`;

  if (extras && extras.trim().length > 0) {
    prompt += `\n${extrasH} ${extras.trim()}.`;
  }

  return prompt;
}
